import { Box, Chip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { RequestButton, RequestEsgButton } from 'modules/common';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { creditReviewInitialSubscription } from 'shared/constants/creditReview/creditReviewInitialSubscription';
import { orderTypes } from 'shared/constants/orderTypes';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { useCofaceContract } from 'shared/hooks/useCofaceContract/useCofaceContract';
import { theme } from 'theme';
import { getShopProducts } from 'modules/shop/selectors/shopSelectors';
import { InfoTooltip } from 'ui';
import { entityPortfolioModuleData } from 'shared/functions/entityPortfolioModuleData';
import { EntityScoreProps, EsgStore, AuthData, AuthStore } from '../../types';
import { ScoreLock } from './ScoreLock';

const useStyles = makeStyles({
  chipCustomScore: {
    color: theme.palette.custom.white,
    backgroundColor: theme.palette.custom.darkGrayAlt,
  },
  tooltip: {
    marginLeft: 3,
  },
});

export const EntityScore: FC<EntityScoreProps> = ({
  entity,
  module,
  subscore,
  onRequestEsg,
  onRequestSentiment,
  onRequestCreditReview,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { cofaceAccessType } = useCofaceContract();
  const esgRequestPermitted = useSelector<EsgStore, boolean>(
    ({ esgContract: { esgRequestPermission } }) => esgRequestPermission,
  );

  const { data: subscriptionsData } = useSelector(getShopProducts);

  const creditReviewSubscription = creditReviewInitialSubscription(subscriptionsData);

  const navigateToCreditReviewSubscription = () => {
    window.location.href = `/ca/subscriptions?productId=${
      creditReviewSubscription ? creditReviewSubscription.id : undefined
    }`;
  };

  const sigProduct = useSelector<AuthStore, AuthData>(({ auth }) => auth.data);

  const { score, canRequest, canUnlock, requested, product, orderType } = entityPortfolioModuleData(
    entity,
    sigProduct,
    cofaceAccessType,
    esgRequestPermitted,
    subscore,
  )[module];

  const {
    meta: { esgCustomScore },
  } = entity;

  /* SCORE - Score is available to show */
  if (score) {
    const displayCustomScore = esgCustomScore && module === ratingTypes.ESG_IMPACT;

    return (
      <Box display="flex" alignItems="center">
        {module === 'climate-stress-test' ? (
          `${score}%`
        ) : (
          <Chip
            label={score}
            variant={displayCustomScore ? 'filled' : 'outlined'}
            className={displayCustomScore ? classes.chipCustomScore : undefined}
            color="primary"
          />
        )}
        {displayCustomScore && (
          <InfoTooltip title={t('ESG.CUSTOM_SCORE_TOOLTIP')} iconClassName={classes.tooltip} />
        )}
      </Box>
    );
  }

  /* EMPTY - There is no main score, so subscores will not be included in any action */
  if (subscore) {
    return <></>;
  }

  /* REQUEST - There is no score, but user has the appropriate subscriptions to request it, or the score was already requested */
  if (canRequest || requested) {
    let requestButton: React.ReactElement | null = null;

    if (module === ratingTypes.ESG_IMPACT) {
      requestButton = (
        <RequestEsgButton onRequestClick={onRequestEsg} alreadyRequested={requested} />
      );
    } else if (module === ratingTypes.SENTIMENT) {
      requestButton = (
        <RequestEsgButton alreadyRequested={requested} onRequestClick={onRequestSentiment} />
      );
    } else if (product && orderType) {
      requestButton = (
        <RequestButton
          id={entity.artifactId}
          product={product}
          orderType={orderType}
          alreadyRequested={requested}
          ratingType={module}
          confirmationModalOpen={orderType === orderTypes.SIG_ENTITY ? onRequestCreditReview : null}
        />
      );
    }

    return requestButton;
  }

  /* UNLOCK - User does not have any subscription to request for score */
  if (canUnlock && module !== ratingTypes.ESG_IMPACT) {
    return (
      <ScoreLock
        entity={entity}
        module={module}
        customHandler={
          module === ratingTypes.CREDIT_REVIEW ? navigateToCreditReviewSubscription : undefined
        }
      />
    );
  }

  /* N/A - Score is not available to request for this entity */
  return <>N/A</>;
};
